<template>
    <div>

        <vue-headful title="Average Inspection Score Charts :: MeritEHS"/>

        <h4 class="py-3 mb-4 d-print-none">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active">Average Inspection Score Charts</li>
            </ol>
        </h4>

        <div :class="pdf_view ? 'container' : ''" :style="pdf_view ? 'max-width: 980px;' : ''">
            <div class="row d-none d-print-block mb-4" v-if="pdfLogo" style="margin-bottom: -20px; z-index: 100;">
                <div class="col-md-3">
                    <img :src="pdfLogo" class="img-fluid" />
                </div>
            </div>

            <div class="alert alert-info d-print-none" v-if="pdf_view">
                <strong>How to Export as PDF:</strong>
                <ol>
                    <li>On your browser, there should be a "File" or "Options" tab with a "Print" option. You may also hold down the "Ctrl" key and press the "P" key.</li>
                    <li>This will bring up the print page options box. There should be an option called "Select Printer" or "Destination".</li>
                    <li>Select "Save as PDF" or "Adobe PDF".</li>
                    <li>Click "Save" or "Print". This will open your file explorer and let you save it to your computer as a PDF.</li>
                </ol>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="d-print-none">
                        <button class="btn btn-sm btn-default" v-on:click="show_avg_filters = !show_avg_filters" title="Show/Hide Filters"><i class="fa fa-filter"></i>
                        <span class="badge" style="background-color: #d9534f" :title="`${filterCount} Filter(s) Applied`" v-if="filterCount > 0">{{ filterCount }}</span>
                        </button>
                        Avg Inspection Score Filters:
                        <div v-show="show_avg_filters" class="mt-3">
                            <div class="d-inline-block">
                                <multi-select v-model="filters.graph_type" :btnLabel="(values) => ('Graph Type: ' + (values.length > 0 ? ' - ' + values[0].name : ''))" :selectOptions="graphTypeOptions" :options="{multi: false}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters.interval" :btnLabel="(values) => ('Interval' + (values.length > 0 ? ' - ' + values[0].name : ''))" :selectOptions="intervalOptions" :options="{multi: false}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters.graph_by" :btnLabel="(values) => ('Graph By' + (values.length > 0 ? ' - ' + values[0].name : ''))" :selectOptions="graphByOptions" :options="{multi: false}"></multi-select>
                            </div>
                             <div class="d-inline-block">
                                <multi-select v-model="filters.inspection_forms" :btnLabel="(values) => ('Inspection Forms ('+values.length+')')" :selectOptions="inspection_forms" :options="{multi: true}">
                                    <option v-for="form in inspection_forms" v-bind:value="form.inspection_form_id">{{ form.form_name }}</option>
                                </multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['jobsite.JobsiteId']" :btnLabel="(values) => ('Jobsites ('+values.length+')')" :selectOptions="jobsites" :options="{multi: true}" :search="true"></multi-select>
                            </div>
                             <div class="d-inline-block">
                                <multi-select v-model="filters.divisions" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="divisions" :options="{multi: true}">
                                </multi-select>
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector ref="inspDate" buttonTitle="Date" v-model="filters.date_range" :value="filters.date_range"/>
                            </div>
                            <div style="margin-top: 20px;">
                                <button type="button" class="btn btn-sm btn-default" v-on:click="closeFilters()">
                                    <i class="fa fa-caret-up"></i> Close
                                </button>
                                <button type="button" class="btn btn-sm btn-warning" v-on:click="clearFilters()">
                                    <i class="fa fa-times"></i> Clear
                                </button>
                                <button type="button" class="btn btn-sm btn-success" v-on:click="applyFilters()">
                                    <i class="fa fa-filter"></i> Apply
                                </button>
                                <div class="d-inline-block ml-2">
                                    <b-form-checkbox v-model="pdf_view" :value="true" :unchecked-value="false" plain class="ml-2">PDF View</b-form-checkbox>
                                </div>

                                <div class="d-inline-block ml-2">
                                    <div class="input-group input-group-sm">
                                        <input v-model="height" placeholder="Graph Height" class="form-control form-control-sm" v-on:change="reload()" />
                                        <div class="input-group-addon">px</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="loading" class="mt-3">
                        <i class="fa fa-spin fa-spinner"></i> Initializing
                    </div>
                    <div v-else>
                        <highcharts id="highcharts-canvas" :options="options" min="600" :style="'min-height: '+(this.height < 600 ? 600 : this.height)+'px;'" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Highcharts from 'highcharts';
    import filterExtractor from '@/helpers/TableFilterExtractor';

    export default {
        data: function() {
            return {
                series: [],
                labels: [],
                jobsites: [],
                loading: false,
                show_avg_filters: true,
                filterCount: 0,
                inspection_forms: [],
                divisions: [],
                totals: [],
                pdf_view: false,
                height: 600,
                pdfLogo: this.$store.state.user.pdf_logo,

                filters: {
                    InspectionFormId: null,
                    date_range: 'ytd',
                    interval: [{'name': 'Monthly', 'id': 'Month'}],
                    graph_by: [{'id': 'JobsiteId', 'name': 'Jobsite'}],
                    IncidentId: null,
                    DivisionId: null,
                    JobsiteId: null,
                    inspection_forms: [],
                    divisions: [],
                    'jobsite.JobsiteId': [],
                    graph_type: [{id: 'line', name: 'Line Graph'}],
                },

                intervalOptions: [
                    {id: 'Week', name: 'Weekly'},
                    {id: 'Month', name: 'Monthly'},
                    {id: 'Year', name: 'Yearly'}
                ],

                graphByOptions: [
                    {id: 'JobsiteId', name: 'Jobsite'},
                    {id: 'inspection_form_id', name: 'Inspection Form'},
                ],

                graphTypeOptions: [
                    {id: 'bar', name: 'Bar Chart'},
                    {id: 'line', name: 'Line Graph'},
                ],

                dateLabel: null,
            }
        },

        computed: {
            options(){
                //Setup
                var vm = this;

                return {
                    chart: {
                        events: {
                            render: function() {
                                //Make sure the chart has loaded
                                if(this.legend.offsetWidth == 0)
                                {
                                    return;
                                }

                                //Check if we need to remove an old level
                                if(this.helperLabel)
                                {
                                    this.helperLabel.destroy();
                                }

                                //Build it
                                var text = '<i class="fa fa-question-circle legend-helper" title="Click on a particular legend to Hide the corresponding line graph"></i>';
                                this.helperLabel = this.renderer.label(text, null, null, null, null, null, true)
                                                .css({
                                                    width: '100px',
                                                    color: '#222',
                                                    fontSize: '16px'
                                                })
                                                .attr({
                                                    'stroke': 'silver',
                                                    'stroke-width': 1,
                                                    'r': 2,
                                                    'padding': 3.5
                                                })
                                                .add();

                                //Debugging
                                var xOffset = (-1 * this.legend.legendWidth) - 20;

                                //Align it
                                this.helperLabel.align(Highcharts.extend(this.helperLabel.getBBox(), {
                                    align: 'right',
                                    x: xOffset, // offset
                                    verticalAlign: 'bottom',
                                    y: 0 // offset
                                }), null, 'spacingBox');
                            }
                        },
                        type: this.filters.graph_type[0].id,
                    },
                    title: {
                        text: 'Avg. Inspection Score Charts: ' + this.dateLabel,
                    },
                    xAxis: {
                        categories: this.labels
                    },
                    yAxis: {
                        title: {
                            text: '% of Avg. Inspection Score'
                        },
                        min: 0,
                        max: 110,
                        tickInterval: 10,
                        //tickPixelInterval: 20
                    },
                    plotOptions: {
                        bar: {
                            dataLabels: {
                                enabled: true,
                                formatter: function(){
                                    return this.point.series.name+': '+this.point.y;
                                }
                            }
                        }
                    },
                    legend: {
                        layout: 'horizontal',
                        align: 'right',
                        verticalAlign: 'bottom',
                        x: 0,
                        y: 0,
                        floating: false,
                        borderColor: '#dddddd',
                        borderWidth: 1,
                    },
                    series: this.series,
                    tooltip: {
                        formatter: function() {
                            //Send it
                            var html = 'Type: <b>'+ this.series.name + '</b><br/>' +
                                    'Date: <b>'+ this.x + '</b><br/>' +
                                    'Score: <b>'+ this.y.toFixed(2) + '%</b><br/>'+
                                    'Total Insp: <b>'+this.point.total_inspections+'</b>';

                            return html;
                        }
                    },
                }
            }
        },

        watch: {
            'filters.inspection_forms'(newValue, oldValue){
                //Update the count
                this.updateFilterCount();
            },

            'filters.date_range'(newVal, oldVal){
                //Set the label
                this.setDateLabel();
            },

            pdf_view(newVal, oldVal){
                //Helper?
                this.loading = true;

                //Redraw the Draw
                this.getData();
            },
        },

        methods: {
            setDateLabel(){
                //Set the Label
                this.dateLabel = this.$refs.inspDate.getLabel();
            },

            updateFilterCount() {
                //Reset the count
                this.filterCount = 0;
                var status = false;

                // Loop over objects in this.filters
                for (var obj in this.filters) {

                    if (this.filters[obj] == null || this.filters[obj] === '') {
                        continue;
                    }

                    if (Array.isArray(this.filters[obj])) {
                        if (this.filters[obj].length > 0) {
                            this.filterCount++;
                        }
                        continue;
                    }

                    if (typeof this.filters[obj] === 'object') {
                        var Empty = true;
                        for (var key in this.filters[obj]) {
                            if (this.filters[obj].hasOwnProperty(key));
                            Empty = false;
                        }
                        if (!Empty) {
                            this.filterCount++;
                        }
                        continue;
                    }

                    if (typeof this.filters[obj] === 'string' || typeof this.filters[obj] === 'number') {
                        this.filterCount++;
                    }
                }
            },

            clearFilters() {
                //Reset each
                this.filters.interval = [{'name': 'Monthly', 'id': 'Month'}];
                this.filters.inspection_forms = [];
                this.filters.divisions = [];
                this.filters['jobsite.JobsiteId'] = [];
                this.filters.date_range = 'ytd';
                this.filters.graph_by = [{'id': 'JobsiteId', 'name': 'Jobsite'}];

                //Rerend
                this.$refs.inspDate.setValue('ytd');
                this.$refs.inspDate.updateFilter();
                //this.$forceUpdate();

                //Update the count
                this.updateFilterCount();

                //Close it
                //this.closeFilters();
                this.applyFilters();
            },

            applyFilters() {
                //Send it
                //this.$refs.partTable.getData();
                this.$emit('filtersChanged', this.filters);

                //Update the count
                this.updateFilterCount();

                //Get the data
                this.getData();

                //Close it
                //this.closeFilters(); //Update the state
            },

            closeFilters() {
                //Hide the modal
                this.show_avg_filters = false;
            },

            reload(){
                //Set it
                this.loading = true;

                //Set the Timeout
                setTimeout(() => {
                    //Reset
                    this.loading = false;
                }, 1000);
            },

            getData() {
                //Setup
                var filters = filterExtractor.extractFilters(this.filters);

                // filter['inspection_forms'] = this.filters['inspection_forms'].map(e => e.id);
                // filter['divisions'] = this.filters['divisions'].map(e => e.id);
                // filter['interval'] = this.filters['interval'][0].id;
                // filter['date_range'] = this.filters['date_range'];
                // filter['graph_by'] = this.filters['graph_by'][0].id;

                //Send it
                this.$http
                    .get('reports/avg-score-stats', filters)
                    .then(response => {
                        //Check Length
                        if(response.data.series.length === 0)
                        {
                            //Alert the user
                            this.$toastr.warning('There is no data for your currently selected filters.')

                            //Stop here
                            return;
                        }

                        this.totals = response.data.totals;
                        //this.avgInspScore = response.data.avgInspScore;

                        //Set all the data
                        this.labels = response.data.labels;
                        this.series = response.data.series;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            getFilters() {

                //Send the response
                this.$http
                    .get('reports/filters/avg-score', this.filters)
                    .then(response => {
                        //Set all the data
                        this.jobsites = response.data.jobsites;
                        this.divisions = response.data.divisions;
                        this.inspection_forms = response.data.inspection_forms;

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    })
                    .then(() => {

                    });
            },
        },

        mounted() {
            this.setDateLabel();
            this.getData();
            this.getFilters();
        }
    }
</script>

<style type="text/css" scoped>
    .legend-helper {
        position: absolute;
        left: -20px;
    }

    @media print{
        /*@page {
            size: landscape;
        }*/

        #highcharts-canvas {
            width: 2300;
        }

        .card {
            background-color: transparent;
            border: none;
        }
    }
</style>